import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { useQueryClient } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import useManageEisRules from '../../_data_sources/useManageEisRules';
import { useEisRuleDetailsContext } from '../../context';
import RuleForm from '../../_components/RuleForm';
import Loading from 'views/App/Portal/Loading';
import type { UpdateEisRuleRequest } from '../../_types';

const EditEisRule: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { rule, isLoading } = useEisRuleDetailsContext();
  const { updateRule } = useManageEisRules();
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  useEffect(() => {
    if (!hasPermission('manageFieldBank', currentOrganizationId || '')) {
      navigate('/admin/performance-reporting/early-intervention-system');
    }
  }, [hasPermission, currentOrganizationId, navigate]);

  const handleSave = async (formData: UpdateEisRuleRequest) => {
    if (!rule) return;

    await updateRule(
      rule.id,
      formData,
      () => {
        enqueueSnackbar(t('performanceReporting.eis.edit.success'), {
          variant: 'success',
          autoHideDuration: 3000,
        });
        queryClient.invalidateQueries(['eisRule', rule.id]);
        queryClient.invalidateQueries(['eisRules']);
        navigate('..');
      },
      (error) => {
        enqueueSnackbar(t('performanceReporting.eis.edit.error'), {
          variant: 'error',
          autoHideDuration: 5000,
        });
        setServerErrors(error);
      }
    );
  };

  const handleCancel = () => {
    navigate('..');
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!rule) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.eis.edit.pageTitle')}
        backUrl='..'
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>{t('performanceReporting.eis.edit.error')}</Box>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={t('performanceReporting.eis.edit.pageTitle')}
      backUrl='..'
    >
      <Box>
        <RuleForm
          onSave={handleSave}
          onCancel={handleCancel}
          serverErrors={serverErrors}
          setServerErrors={setServerErrors}
          initialValues={rule}
          isEditMode={true}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default EditEisRule;
